import styled from '@emotion/styled';
import { Button, Drawer, Input, Space, Table, Tag } from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { FaEyeSlash } from 'react-icons/fa';
import { MdClose } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { PulseLoader } from 'react-spinners';
import { ConnectedUserEntity } from '../../../../entities/ConnectedUserEntity';
import { UserEntity } from '../../../../entities/utilisateur.entity';
import { PRIMARY, YELLOW } from '../../../../shared/colors';
import { Layout } from '../../../shared/Layout';
import { PasswordForm } from '../components/PasswordForm';
import { getUtilisateurDetails } from '../network';

const Container = styled.div`
  height: calc(100vh - 30px);
  display: flex;
  background-color: #1c2025;
  color: white;

  .bread-crum span {
    color: white;
  }

  td.ant-table-column-sort {
    background-color: transparent !important;
  }

  th {
    background-color: ${PRIMARY} !important;
    color: white !important;
    font-weight: bold;
  }

  .odd-row {
    background-color: #757575;
    color: white;
  }

  .even-row {
    background-color: #acacac;
    color: white;
  }

  .odd-row,
  .even-row {
    &:hover > td {
      background-color: ${YELLOW} !important;
      cursor: pointer;
      color: black; /* plus lisible */
    }
  }

  .content {
    padding: 50px;
    width: 100%;
    background-color: #1c2025;
    color: white;
  }
`;

export const GestionPasswordPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [showDrawer, setShowDrawer] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [filterUsers, setFilterUsers] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserEntity>();
  const connectedUser: ConnectedUserEntity = useSelector(
    (state: any) => state.userReducer,
  ).user;

  const fetchData = () => {
    setIsLoading(true);
    getUtilisateurDetails(connectedUser.id).then(({ data }) => {
      setUsers(data);
      setFilterUsers(data);
      setIsLoading(false);
    });
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setColor = (code: string) => {
    switch (code) {
      case 'admin':
        return '#f50';
      case 'resp_commercial':
        return '#3c096c';
      case 'commercial':
        return '#f9a825';
      case 'responsable':
        return '#108ee9';
      case 'chef_agence':
        return '#8d6e63';
      case 'gestionnaire':
        return '#5f0f40';
      case 'sadmin':
        return '#fb8b24';
    }
  };

  const COLUMNS = [
    {
      title: 'Nom',
      dataIndex: 'nom',
      flex: 1,
    },
    {
      title: 'Prénom',
      dataIndex: 'prenom',
      flex: 1,
    },
    {
      title: 'Adresse',
      dataIndex: 'adresse',
      flex: 1,
    },
    {
      title: 'Agence',
      render: (cell: any, row: any) => row.agence?.nom,
      flex: 1,
    },
    {
      title: 'Partenaire',
      render: (cell: any, row: any) => row.partner?.nom,
      flex: 1,
    },
    {
      title: 'Date création',
      dataIndex: 'created_at',
      render: (cell: any) => `${moment(cell).format('DD/MM/YYYY')}`,
      flex: 1,
    },
    {
      title: 'Date modification',
      dataIndex: 'updated_at',
      render: (cell: any) => `${moment(cell).format('DD/MM/YYYY')}`,
      flex: 1,
    },
    {
      title: 'Profils',
      render: (cell: any, row: any) => {
        return (
          <Space>
            {row.profils.length === 0 ? (
              <Tag>--</Tag>
            ) : (
              row.profils.map((pro: any) => (
                <Tag key={pro.id} color={setColor(pro.code_profil)}>
                  {pro.nom}
                </Tag>
              ))
            )}
          </Space>
        );
      },
      flex: 1,
    },
    {
      title: 'Statut',
      render: (cell: any, row: any) => (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          {row.actif ? (
            <div
              style={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                backgroundColor: 'green',
              }}
            ></div>
          ) : (
            <div
              style={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                backgroundColor: 'red',
              }}
            ></div>
          )}
        </div>
      ),
      flex: 1,
    },
    {
      title: 'Action',
      render: (cell: any, row: any) => (
        <Space>
          <Button
            icon={<FaEyeSlash style={{ color: 'white', marginBottom: -1 }} />}
            style={{
              backgroundColor: 'transparent',
              borderRadius: 20,
              zoom: 0.8,
              borderColor: 'white',
            }}
            onClick={() => {
              setSelectedUser(row);
              setShowDrawer(true);
            }}
          />
        </Space>
      ),
      flex: 1,
    },
  ];

  return (
    <Layout>
      <Container>
        <div className='content'>
          <div style={{ marginTop: 0 }}>
            <h2 style={{ color: 'white' }}>Gestion des mots de passe</h2>

            <div style={{ marginBottom: 5 }}>
              <Input
                placeholder='Search...'
                style={{ width: 250, marginRight: 5 }}
                onChange={(e) => {
                  setFilterUsers(
                    users.filter(
                      (u) =>
                        u.nom
                          .toLowerCase()
                          .includes(e.target.value.toLowerCase()) ||
                        u.prenom
                          .toLowerCase()
                          .includes(e.target.value.toLowerCase()) ||
                        u.agence?.nom
                          .toLowerCase()
                          .includes(e.target.value.toLowerCase()) ||
                        u.partner?.nom
                          .toLowerCase()
                          .includes(e.target.value.toLowerCase()) ||
                        u.adresse
                          .toLowerCase()
                          .includes(e.target.value.toLowerCase()),
                    ),
                  );
                }}
              />
              <PulseLoader
                size={5}
                margin={2}
                color={YELLOW}
                loading={isLoading}
              />
            </div>

            <Table
              columns={COLUMNS}
              dataSource={filterUsers}
              rowClassName={(row, index) =>
                index % 2 === 0 ? 'even-row' : 'odd-row'
              }
              size='small'
              style={{ zoom: 0.9 }}
              pagination={false}
              loading={isLoading}
            />
          </div>
        </div>
        <Drawer
          title='Réinitialiser le mot de passe'
          placement='right'
          width={400}
          onClose={() => setShowDrawer(false)}
          open={showDrawer}
          closeIcon={<MdClose size={20} />}
        >
          <PasswordForm
            user={selectedUser as UserEntity}
            setShowDrawer={setShowDrawer}
          />
        </Drawer>
      </Container>
    </Layout>
  );
};
